import React from "react";
import { Mail, CheckCircle, AlertTriangle, Clock } from "react-feather";
import dayjs from "dayjs";
import SectionCard from "../../../components/common/SectionCard";
import { useGetCustomerFeedbackQuery } from "../serviceFeedbackApi";

/*
  What the customer was told about this job, and what they said back (Batch184).

  Shown on the job's own detail page rather than as a separate report, because the
  question it answers - "does the customer know, and are they happy?" - is only ever
  asked while looking at the job.

  Deliberately shows SKIPPED and FAILED rows, not just successful sends. Around 88% of
  customers have no email address on file, so "the customer was informed" is a claim that
  has to be verifiable. A panel that only listed successes would quietly imply everyone
  had been told.

  Renders nothing at all when there is no trail - a job from before this feature, or one
  whose branch has no customer emails configured, should not grow an empty card.
*/

const STAGE_LABEL = {
  SCHEDULED: "Scheduled",
  IN_PROGRESS: "In progress",
  ASSIGNED: "Registered / technician assigned",
  ONGOING: "In progress",
  ON_HOLD: "On hold",
  COMPLETED: "Completed",
  VISIT: "Visit record",
};

const SEND_STYLE = {
  SENT: { label: "Sent", cls: "badge-linesuccess" },
  SKIPPED: { label: "Not sent", cls: "text-bg-secondary" },
  FAILED: { label: "Failed", cls: "badge-linedanger" },
};

const CustomerFeedbackPanel = ({ sourceRecordGuid }) => {
  const { data: rows = [], isLoading } = useGetCustomerFeedbackQuery(sourceRecordGuid, {
    skip: !sourceRecordGuid,
  });

  if (isLoading || rows.length === 0) return null;

  const answered = rows.filter((r) => r.response);
  const confirmed = answered.filter((r) => r.response === "CONFIRMED").length;
  const issues = answered.filter((r) => r.response === "ISSUE").length;

  return (
    <SectionCard
      icon={Mail}
      title="Customer Updates"
      right={
        issues > 0 ? (
          <span className="badge badge-linedanger">
            <AlertTriangle size={12} className="me-1" />
            {issues} issue{issues > 1 ? "s" : ""} reported
          </span>
        ) : confirmed > 0 ? (
          <span className="badge badge-linesuccess">
            <CheckCircle size={12} className="me-1" /> Confirmed by customer
          </span>
        ) : (
          <span className="badge text-bg-secondary">
            <Clock size={12} className="me-1" /> Awaiting reply
          </span>
        )
      }
    >
      <div className="table-responsive">
        <table className="table table-sm align-middle mb-0">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Email</th>
              <th>Sent</th>
              <th>Customer response</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const send = SEND_STYLE[row.sendStatus] || SEND_STYLE.SKIPPED;
              return (
                <tr key={row.recordGuid}>
                  <td className="fw-semibold">{STAGE_LABEL[row.eventType] || row.eventType}</td>
                  <td>
                    <span className={`badge ${send.cls}`}>{send.label}</span>
                    <div className="small text-muted">{row.sentToEmail || "no address on file"}</div>
                    {/* The reason matters more than the status - "no address on file"
                        is a data problem someone can fix, an SMTP refusal is not. */}
                    {row.sendStatus !== "SENT" && row.sendDetail && (
                      <div className="small text-muted fst-italic">{row.sendDetail}</div>
                    )}
                  </td>
                  <td className="small text-muted">
                    {row.sentDate ? dayjs(row.sentDate).format("DD MMM YYYY HH:mm") : "-"}
                  </td>
                  <td>
                    {row.response === "CONFIRMED" && (
                      <span className="badge badge-linesuccess">
                        <CheckCircle size={12} className="me-1" /> Confirmed
                      </span>
                    )}
                    {row.response === "ISSUE" && (
                      <span className="badge badge-linedanger">
                        <AlertTriangle size={12} className="me-1" /> Issue reported
                      </span>
                    )}
                    {!row.response && <span className="text-muted small">-</span>}
                    {row.respondedDate && (
                      <div className="small text-muted">{dayjs(row.respondedDate).format("DD MMM YYYY HH:mm")}</div>
                    )}
                    {row.responseRemarks && (
                      <div className="small mt-1" style={{ color: "#1F2A37" }}>
                        &ldquo;{row.responseRemarks}&rdquo;
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

export default CustomerFeedbackPanel;
